'use client';

import { useState, useCallback, useEffect } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { useTrack } from '@owlcat/app-shell';
import { useTelegramMiniApp } from '@/contexts/authentication/client';
import { openSubscription } from '../lib/accountsTopUp';
import { getAllowanceMeter } from '../actions/getAllowanceMeter';

/**
 * The "premium creations exhausted" UPGRADE SHEET (subscription-first model).
 *
 * Fired by the server's 'tokens.insufficient' SSE event, framed as UPGRADE (no
 * numeric balance / token wording). On open it reads the allowance summary to pick
 * the FLEET-WIDE wall variant — the same two-state approach every app should use:
 *   • NO live subscription (onboarding/bonus spent) → "Subscribe to keep creating".
 *   • HAS a subscription (monthly grant spent)      → "Plan limit reached — upgrade,
 *     or it refills in N days" (nextExpiry).
 * Auto-dismisses when the wallet refills ('subscription.balance.updated').
 */
export function InsufficientTokensPopup() {
  const [open, setOpen] = useState(false);
  const [subbed, setSubbed] = useState(false);
  const [nextExpiry, setNextExpiry] = useState<string | null>(null);
  const t = useTranslations('subscription.allowance');
  const locale = useLocale();
  const { webApp } = useTelegramMiniApp();

  const track = useTrack();
  useEventSourceEvent(
    'tokens.insufficient',
    useCallback((payload: Record<string, unknown>) => {
      setOpen(true);
      // Fleet analytics: which action hit the allowance wall (op type only, no amounts).
      track('allowance_exhausted', { app_id: 'portal', action: String(payload?.operationType ?? 'unknown') });
      // Pick the wall variant from the live summary (subscribed vs not).
      getAllowanceMeter()
        .then((d) => {
          if (d) {
            setSubbed(d.hasSubscription);
            setNextExpiry(d.nextExpiry);
          }
        })
        .catch(() => {
          /* best-effort — falls back to the no-subscription copy */
        });
    }, [track]),
  );

  // Auto-dismiss when the allowance is replenished (top-up / refill).
  useEventSourceEvent(
    'subscription.balance.updated',
    useCallback(() => {
      setOpen(false);
    }, []),
  );

  /**
   * A top-up happens in ACCOUNTS, which emits no portal event.
   *
   * So the sheet that sent the parent there had no way of knowing they had
   * paid: they came back with a full allowance and the wall was still across
   * the screen. Coming back to the tab is the moment to check, and the sheet
   * closes itself when there is something to spend again.
   */
  useEffect(() => {
    if (!open) return;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      void getAllowanceMeter()
        .then((d) => {
          if (d?.hasAllowance) setOpen(false);
        })
        .catch(() => undefined);
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [open]);

  const title = subbed ? t('exhaustedPlanTitle') : t('exhaustedNoSubTitle');
  const body = subbed
    ? t('exhaustedPlanBody', { when: refillWhen(nextExpiry, locale, t('refillSoon')) })
    : t('exhaustedNoSubBody');
  const cta = subbed ? t('upgradePlan') : t('seePlans');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="insufficient-tokens-popup" className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="size-5 text-primary shrink-0" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="insufficient-tokens-not-now">
            {t('notNow')}
          </Button>
          <Button
            onClick={() => { setOpen(false); openSubscription(locale, webApp); }}
            data-testid="insufficient-tokens-buy-button"
            className="gap-1.5"
          >
            {cta}
            <ArrowRight className="size-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Locale-aware "in N days / hours" for the plan refill date; `fallback` when unknown. */
function refillWhen(iso: string | null, locale: string, fallback: string): string {
  if (!iso) return fallback;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return fallback;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const days = Math.round((then - Date.now()) / 86_400_000);
  if (Math.abs(days) >= 1) return rtf.format(days, 'day');
  const hours = Math.round((then - Date.now()) / 3_600_000);
  return rtf.format(hours, 'hour');
}
